import Done from '@mui/icons-material/Done'
import ErrorIcon from '@mui/icons-material/Error'
import InstallMobile from '@mui/icons-material/InstallMobile'
import {CircularProgress, ListItemIcon, ListItemText, MenuItem, type MenuItemProps} from '@mui/material'
import {memo, type ReactNode, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import type {Timer} from '../../../../@types/timeout'
import {AsyncStateError, AsyncStateInit, AsyncStateLoading, AsyncStateSuccess} from '../../../../enums/asyncState'
import {useAsyncSubmit} from '../../../../hooks/useAsyncSubmit'
import {useNotification} from '../../../../hooks/useNotification'
import {useAsyncState, withAsyncContext} from '../../../../states/AsyncContext'
import {useInstallPrompt} from '../../../../states/installPrompt'
import {SilentError} from '../../../../utils/ApiError'
import {isDesktop} from '../../../../utils/device'

interface AsyncMenuProps extends MenuItemProps {
  successTtl?: number
  errorTtl?: number
  icon?: ReactNode
}

const AsyncMenu = memo(({
  successTtl = 2_000,
  errorTtl = 5_000,
  icon,
  children,
  ...props
}: AsyncMenuProps) => {
  const state = useAsyncState()
  const [displayState, setDisplayState] = useState(true)
  const timer = useRef<Timer>(undefined)

  useEffect(() => {
    setDisplayState(true)
    clearTimeout(timer.current)
    if ([AsyncStateLoading, AsyncStateInit].includes(state)) {
      return
    }
    timer.current = setTimeout(() => {
      setDisplayState(false)
    }, state === AsyncStateError ? errorTtl : successTtl)

    return () => {
      clearTimeout(timer.current)
    }
  }, [errorTtl, state, successTtl])

  const color = useMemo((): undefined | 'error' | 'success' => {
    if (state === AsyncStateLoading) {
      return undefined
    }
    if (!displayState) {
      return undefined
    }
    if (state === AsyncStateError && errorTtl > 0) {
      return 'error'
    }
    if (state === AsyncStateSuccess && successTtl > 0) {
      return 'success'
    }

    return undefined
  }, [displayState, errorTtl, state, successTtl])

  const loadingIcon = useMemo(() => {
    if (state === AsyncStateLoading) {
      return <CircularProgress size={24}/>
    }
    if (!displayState) {
      return undefined
    }
    if (state === AsyncStateError && errorTtl > 0) {
      return <ErrorIcon color={color}/>
    }
    if (state === AsyncStateSuccess && successTtl > 0) {
      return <Done color={color}/>
    }

    return undefined
  }, [color, displayState, errorTtl, state, successTtl])

  return <MenuItem disabled={state === AsyncStateLoading} {...props} >
    <ListItemIcon>{loadingIcon ?? icon}</ListItemIcon>
    <ListItemText primary={children} primaryTypographyProps={{color: color}}/>
  </MenuItem>
})

export const InstallPwaMenu = withAsyncContext(memo(() => {
  const {t} = useTranslation()
  const installPrompt = useInstallPrompt()
  const {notifyInfo} = useNotification()

  const handleSubmit = useAsyncSubmit(async () => {
    if (!installPrompt) {
      return
    }

    await installPrompt.prompt()
    const {outcome} = await installPrompt.userChoice
    if (outcome === 'accepted') {
      notifyInfo(t('common.pwa.notification.accepted'))
    } else {
      notifyInfo(t('common.pwa.notification.canceled'))
      throw new SilentError('Installation canceled')
    }
  })

  if (isDesktop) {
    return null
  }
  if (!installPrompt) {
    return null
  }

  return <AsyncMenu onClick={handleSubmit} icon={<InstallMobile/>}>
    {t('common.pwa.buttonLong')}
  </AsyncMenu>
}))
